import { z } from 'zod';
import { timestampSchema } from './admin.js';
import { addDays, daysBetween } from './calendar/dates.js';
import { isoDateSchema, projectStatusSchema, type StatusCategory } from './projects.js';
import { richTextSchema } from './rich-text.js';
import { formatTaskKey, taskTypeSchema, type TaskType } from './tasks.js';

/**
 * REP-01..06, E-41: project status reports. A report is written by the project lead for an
 * internal meeting or for the customer. The facts (progress, milestones, changes, risks) are
 * computed from the project; traffic light, summary and decisions are entered by hand. A final
 * report keeps the facts as they were when it was finalized (`snapshot`).
 */

/** `customer` only ever contains what external members of the project may see (docs/04). */
export const reportAudienceSchema = z.enum(['internal', 'customer']);
export type ReportAudience = z.infer<typeof reportAudienceSchema>;

export const reportStatusSchema = z.enum(['draft', 'final']);
export type ReportStatus = z.infer<typeof reportStatusSchema>;

export const trafficLightSchema = z.enum(['green', 'yellow', 'red']);
export type TrafficLight = z.infer<typeof trafficLightSchema>;

/** Calendar days of the default period of a first report (today included). */
export const REPORT_DEFAULT_DAYS = 14;
/** Longest period of a report. */
export const REPORT_MAX_PERIOD_DAYS = 366;
/** Look-ahead of "next steps" in calendar days from today. */
export const REPORT_NEXT_STEPS_DAYS = 14;
/** Look-ahead of upcoming milestones in calendar days from today. */
export const REPORT_MILESTONE_DAYS = 28;
/** Entries per list; `total` tells how many there are. */
export const REPORT_SECTION_LIMIT = 50;

/** Problem `type` URIs of reports, so the UI can show specific messages. */
export const reportProblemTypes = {
  final: 'urn:pm-tool:problem:report-final',
  trafficLightMissing: 'urn:pm-tool:problem:report-traffic-light-missing',
  invalidPeriod: 'urn:pm-tool:problem:report-invalid-period',
} as const;

// --- Content (computed for drafts, stored as snapshot when final) ------------------------------

/** A person as the audience may see them; `name: null` = internal person hidden (E-08). */
export const reportPersonSchema = z.object({ name: z.string().nullable() });
export type ReportPerson = z.infer<typeof reportPersonSchema>;

export const reportTaskRefSchema = z.object({
  key: z.string(),
  title: z.string(),
  type: taskTypeSchema,
  dueDate: isoDateSchema.nullable(),
  assignee: reportPersonSchema.nullable(),
});
export type ReportTaskRef = z.infer<typeof reportTaskRefSchema>;

function reportListSchema<T extends z.ZodType>(item: T) {
  return z.object({ items: z.array(item), total: z.number().int().min(0) });
}

export const reportDateChangeSchema = z.object({
  task: reportTaskRefSchema,
  from: isoDateSchema.nullable(),
  to: isoDateSchema.nullable(),
});

export const reportConflictSchema = z.object({
  predecessor: reportTaskRefSchema,
  successor: reportTaskRefSchema,
});

export const reportContentSchema = z.object({
  version: z.literal(1),
  generatedAt: timestampSchema,
  /** Day the facts were computed for (Europe/Berlin). */
  today: isoDateSchema,
  project: z.object({
    key: z.string(),
    name: z.string(),
    customer: z.string().nullable(),
    lead: reportPersonSchema.nullable(),
    status: projectStatusSchema,
    startDate: isoDateSchema.nullable(),
    endDate: isoDateSchema.nullable(),
  }),
  /** Top-level tasks without milestones, like the project progress (PROJ-05). */
  progress: z.object({
    total: z.number().int(),
    todo: z.number().int(),
    inProgress: z.number().int(),
    done: z.number().int(),
  }),
  /** Latest due date of all tasks and milestones in the report. */
  plannedEnd: isoDateSchema.nullable(),
  milestones: z.object({
    reached: reportListSchema(reportTaskRefSchema),
    upcoming: reportListSchema(reportTaskRefSchema),
    overdue: reportListSchema(reportTaskRefSchema),
  }),
  completed: reportListSchema(reportTaskRefSchema),
  dateChanges: reportListSchema(reportDateChangeSchema),
  nextSteps: reportListSchema(reportTaskRefSchema),
  overdue: reportListSchema(reportTaskRefSchema),
  conflicts: reportListSchema(reportConflictSchema),
  /** Only in internal reports. */
  internal: z
    .object({
      unassigned: reportListSchema(reportTaskRefSchema),
      unansweredComments: reportListSchema(
        z.object({ task: reportTaskRefSchema, since: timestampSchema }),
      ),
      /** Overbooked people in the next weeks (DASH-04); null without resource.view. */
      overbookedPeople: z.number().int().nullable(),
    })
    .nullable(),
});
export type ReportContent = z.infer<typeof reportContentSchema>;

// --- Computation (pure) ------------------------------------------------------------------------

export type ReportTaskInput = {
  id: string;
  parentId: string | null;
  number: number;
  type: TaskType;
  title: string;
  statusCategory: StatusCategory;
  dueDate: string | null;
  /** Day the task was completed (Europe/Berlin), null when open. */
  completedOn: string | null;
  /** Already resolved for the audience (E-08). */
  assignee: ReportPerson | null;
};

/** One activity entry that changed a due date, oldest first. */
export type ReportDateChangeInput = {
  taskId: string;
  /** Day of the change (Europe/Berlin). */
  on: string;
  from: string | null;
  to: string | null;
};

export type ReportInput = {
  generatedAt: string;
  today: string;
  period: { from: string; to: string };
  project: ReportContent['project'];
  /** Only the tasks of the audience (customer: external tasks with external parents). */
  tasks: readonly ReportTaskInput[];
  dateChanges: readonly ReportDateChangeInput[];
  /** Violated dependencies of the whole project; only those between reported tasks are kept. */
  conflicts: readonly { predecessorId: string; successorId: string }[];
  /** Internal reports only. */
  internal: {
    unansweredComments: readonly { taskId: string; since: string }[];
    overbookedPeople: number | null;
  } | null;
};

function limited<T>(items: readonly T[]) {
  return { items: items.slice(0, REPORT_SECTION_LIMIT), total: items.length };
}

const byDueDate = (a: ReportTaskInput, b: ReportTaskInput) =>
  (a.dueDate ?? '9999-12-31').localeCompare(b.dueDate ?? '9999-12-31') || a.number - b.number;

const within = (date: string | null, from: string, to: string) =>
  date !== null && date >= from && date <= to;

/** REP-03: the facts of a report from the tasks of its audience. */
export function buildReportContent(input: ReportInput): ReportContent {
  const { today, period } = input;
  const ref = (task: ReportTaskInput): ReportTaskRef => ({
    key: formatTaskKey(input.project.key, task.number),
    title: task.title,
    type: task.type,
    dueDate: task.dueDate,
    assignee: task.assignee,
  });
  const byId = new Map(input.tasks.map((t) => [t.id, t]));
  const isOpen = (t: ReportTaskInput) => t.statusCategory !== 'done';
  const milestones = input.tasks.filter((t) => t.type === 'milestone').sort(byDueDate);
  const work = input.tasks.filter((t) => t.type === 'task');
  const topLevel = work.filter((t) => t.parentId === null);

  const dueDates = input.tasks.flatMap((t) => (t.dueDate === null ? [] : [t.dueDate])).sort();

  // Net change per task within the period: first `from`, last `to`; unchanged ones are dropped.
  const changes = new Map<string, { from: string | null; to: string | null }>();
  for (const change of input.dateChanges) {
    if (!byId.has(change.taskId) || !within(change.on, period.from, period.to)) continue;
    const seen = changes.get(change.taskId);
    changes.set(change.taskId, {
      from: seen === undefined ? change.from : seen.from,
      to: change.to,
    });
  }
  const dateChanges = [...changes.entries()]
    .flatMap(([taskId, change]) => {
      const task = byId.get(taskId);
      if (task === undefined || change.from === change.to) return [];
      return [{ task, ...change }];
    })
    .sort((a, b) => byDueDate(a.task, b.task));

  const conflicts = input.conflicts.flatMap((c) => {
    const predecessor = byId.get(c.predecessorId);
    const successor = byId.get(c.successorId);
    return predecessor === undefined || successor === undefined ? [] : [{ predecessor, successor }];
  });

  return {
    version: 1,
    generatedAt: input.generatedAt,
    today,
    project: input.project,
    progress: {
      total: topLevel.length,
      todo: topLevel.filter((t) => t.statusCategory === 'todo').length,
      inProgress: topLevel.filter((t) => t.statusCategory === 'in_progress').length,
      done: topLevel.filter((t) => t.statusCategory === 'done').length,
    },
    plannedEnd: dueDates.at(-1) ?? null,
    milestones: {
      reached: limited(
        milestones
          .filter((m) => !isOpen(m) && within(m.completedOn, period.from, period.to))
          .map(ref),
      ),
      upcoming: limited(
        milestones
          .filter(
            (m) => isOpen(m) && within(m.dueDate, today, addDays(today, REPORT_MILESTONE_DAYS)),
          )
          .map(ref),
      ),
      overdue: limited(
        milestones.filter((m) => isOpen(m) && m.dueDate !== null && m.dueDate < today).map(ref),
      ),
    },
    completed: limited(
      work
        .filter((t) => !isOpen(t) && within(t.completedOn, period.from, period.to))
        .sort(
          (a, b) => (a.completedOn ?? '').localeCompare(b.completedOn ?? '') || a.number - b.number,
        )
        .map(ref),
    ),
    dateChanges: limited(dateChanges.map((c) => ({ task: ref(c.task), from: c.from, to: c.to }))),
    nextSteps: limited(
      work
        .filter(
          (t) => isOpen(t) && within(t.dueDate, today, addDays(today, REPORT_NEXT_STEPS_DAYS)),
        )
        .sort(byDueDate)
        .map(ref),
    ),
    overdue: limited(
      work
        .filter((t) => isOpen(t) && t.dueDate !== null && t.dueDate < today)
        .sort(byDueDate)
        .map(ref),
    ),
    conflicts: limited(
      conflicts.map((c) => ({ predecessor: ref(c.predecessor), successor: ref(c.successor) })),
    ),
    internal:
      input.internal === null
        ? null
        : {
            unassigned: limited(
              work
                .filter((t) => isOpen(t) && t.assignee === null)
                .sort(byDueDate)
                .map(ref),
            ),
            unansweredComments: limited(
              input.internal.unansweredComments.flatMap((c) => {
                const task = byId.get(c.taskId);
                return task === undefined ? [] : [{ task: ref(task), since: c.since }];
              }),
            ),
            overbookedPeople: input.internal.overbookedPeople,
          },
  };
}

/**
 * REP-02: default period of a new report. It continues the previous report of the same audience
 * (the day after its end, at most today); a first report covers the last two weeks.
 */
export function defaultReportPeriod(
  previousTo: string | null,
  today: string,
): { from: string; to: string } {
  if (previousTo === null) return { from: addDays(today, 1 - REPORT_DEFAULT_DAYS), to: today };
  const next = addDays(previousTo, 1);
  return { from: next > today ? today : next, to: today };
}

/** Problem detail for an invalid period, or null. */
export function reportPeriodError(period: { from: string; to: string }): string | null {
  if (period.from > period.to) return 'The period must not end before it starts';
  if (daysBetween(period.from, period.to) >= REPORT_MAX_PERIOD_DAYS) {
    return `A report covers at most ${String(REPORT_MAX_PERIOD_DAYS)} days`;
  }
  return null;
}

// --- API ---------------------------------------------------------------------------------------

export const reportIdParamsSchema = z.object({ reportId: z.uuid() });

export const projectReportSummarySchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  audience: reportAudienceSchema,
  status: reportStatusSchema,
  periodFrom: isoDateSchema,
  periodTo: isoDateSchema,
  trafficLight: trafficLightSchema.nullable(),
  createdBy: reportPersonSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
  finalizedAt: timestampSchema.nullable(),
});
export type ProjectReportSummary = z.infer<typeof projectReportSummarySchema>;

export const projectReportListResponseSchema = z.object({
  items: z.array(projectReportSummarySchema),
});
export type ProjectReportListResponse = z.infer<typeof projectReportListResponseSchema>;

export const projectReportSchema = projectReportSummarySchema.extend({
  summary: richTextSchema.nullable(),
  decisions: richTextSchema.nullable(),
  content: reportContentSchema,
});
export type ProjectReport = z.infer<typeof projectReportSchema>;

export const createReportBodySchema = z
  .object({
    audience: reportAudienceSchema,
    /** Both or neither; without them the default period applies (REP-02). */
    periodFrom: isoDateSchema.optional(),
    periodTo: isoDateSchema.optional(),
  })
  .refine((body) => (body.periodFrom === undefined) === (body.periodTo === undefined), {
    message: 'Give both periodFrom and periodTo or neither',
  });
export type CreateReportBody = z.infer<typeof createReportBodySchema>;

/** Drafts only; `null` clears a field. */
export const updateReportBodySchema = z.object({
  periodFrom: isoDateSchema.optional(),
  periodTo: isoDateSchema.optional(),
  trafficLight: trafficLightSchema.nullable().optional(),
  summary: richTextSchema.nullable().optional(),
  decisions: richTextSchema.nullable().optional(),
});
export type UpdateReportBody = z.infer<typeof updateReportBodySchema>;
